import {AfterViewInit, Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {UtilsService} from "../utils.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {ApiRequestService} from "../api-request.service";
import {ApiDataSource} from "../../utils/api-data-source";
import {UserPublicProfileComponent} from "../user-public-profile/user-public-profile.component";
import {AssetTypesEditComponent} from "../../asset-types/asset-types-edit/asset-types-edit.component";
import {RBACService} from "../rbac.service";

@Component({
  selector: 'app-asset-types-selector',
  templateUrl: './asset-types-selector.component.html',
  styleUrls: ['./asset-types-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetTypesSelectorComponent implements OnInit, AfterViewInit {

  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'id',
    'name',
    'description',
    'linked_assets_count',
    'created_by_user',
    'date_created',
    'actions'
  ];
  dataSource: AssetTypesSelectorDataSource;
  returnAllSelected = false;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  selectedAssetType: { id: number; name: string; parent_id: number } | null = null;

  // RBAC permission flag for the in-selector create button. Resolved once in ngOnInit().
  canCreate: boolean = false;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private rbac: RBACService,
    public dialogRef: MatDialogRef<AssetTypesSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // set multiple if it is not present to false
    if (typeof this.data['multiple'] === 'undefined') {
      this.data['multiple'] = false;
    }

    // set required if it is not present to false
    if (typeof this.data['required'] === 'undefined') {
      this.data['required'] = false;
    }

    // set can_create if it is not present to false
    if (typeof this.data['can_create'] === 'undefined') {
      this.data['can_create'] = false;
    }

    // construct the datasource
    this.dataSource = new AssetTypesSelectorDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // set a default selection of nothing if no selected values are passed through
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // loop and select the values
    for (let i = 0; i < this.data['selected'].length; i++) {
      if (this.data['selected'][i]) {
        this.dataSource.selection.select(this.data['selected'][i]);
      }
    }
    this.returnAllSelected = this.data.returnAllSelected;
  }

  ngOnInit() {
    // Check RBAC permission: standalone asset-types:view OR a parent feature that needs asset type
    // selection. Evaluated with exact: false, so any one of these is enough.
    //
    // The asset create/edit dialog opens this selector as a form field (asset type is a required
    // field) and the asset list filter opens it as a filter, so a user who may manage assets but not
    // asset types must still be able to pick one, otherwise the asset form becomes unusable.
    //
    // MUST stay in sync with AssetTypesController::index().
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_VIEW),
      // Assets: asset type is a required field on the create/edit form, and a list filter.
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_EDIT)
    ], false)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view asset types.');
      this.dialogRef.close();
      return;
    }

    // Creating a new asset type from inside the selector: standalone asset-types:create OR an asset
    // permission that needs to author a type in order to complete an asset. The caller still has to
    // opt in via data.can_create, which the asset create/edit form sets and the asset list filter
    // does not, so this never adds a create button to a filter.
    //
    // Asset type is a REQUIRED field on the asset form, so on an account with no asset types a user
    // holding assets create would otherwise be stuck at an empty selector with no way forward.
    //
    // MUST stay in sync with AssetTypesController::store().
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_EDIT)
    ], false);

    // Get the data. Deliberately here rather than in the constructor so the permission check above
    // always runs before the request is made.
    const parent_id = Number(this.data['parent_id']);
    // NOTE: this was previously written as
    //   parent_id ?? parent_id > 0 ?? !this.data['multiple']
    // but `Number()` never returns null/undefined, so both `??` branches were
    // dead code and the whole expression always evaluated to `parent_id`.
    // TypeScript 5.9 now reports that as an error (TS2881). Reduced to the
    // equivalent truthiness check to preserve the existing behaviour exactly.
    // The original intent was probably `parent_id > 0 && !this.data['multiple']`,
    // which is a behaviour change and so deliberately NOT applied here.
    if ( parent_id ) {
      this.onFilterByParent(parent_id);
    } else {
      this.dataSource.getData();
    }
  }

  ngAfterViewInit() {
    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      this.dataSource.getData(false);
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Clears the search bar and performs a search.
   *
   * @returns {void}
   */
  onClearSearch(): void {
    // clear the search field in the datasource
    this.dataSource.search = '';
    // search for records
    this.dataSource.getData(true);
  }

  /**
   * Clear the current selection.
   *
   * @returns {void}
   */
  onClearSelected(): void {
    this.dataSource.selection.clear();
    this.selectedAssetType = null;
  }

  /**
   * Handle the change in asset type selection.
   *
   * @param {boolean} checked - Indicates whether the asset type is checked or not.
   * @param {any} row - The row representing the asset type that was selected.
   * @returns {void}
   */
  onAssetTypeSelectionChange(checked: boolean, row: any): void {
    if (checked) {
      this.dataSource.selection.select(row.id);
    } else {
      this.dataSource.selection.deselect(row.id);
    }

    if (this.dataSource.multiple) {
      return;
    }

    this.selectedAssetType = checked
      ? {
        id: Number(row.id),
        name: row.name || '',
        parent_id: row?.parent_id || 0
      }
      : null;
  }

  /**
   * Handle selection by clicking an Asset Type row.
   *
   * @param row The Asset Type row that was clicked.
   * @returns void
   */
  onAssetTypeRowClick(row: any): void {
    const checked = !this.dataSource.selection.isSelected(row.id);

    this.onAssetTypeSelectionChange(checked, row);
  }

  /**
   * Save the selected data and close the dialog.
   *
   * @return {void}
   */
  onSaveAndClose(): void {
    if (this.returnAllSelected) {
      return this.dialogRef.close({
        selected: this.dataSource.selection.selected,
        isAllSelected: this.dataSource.isAllSelected()
      });
    }

    let response: number | number[] | { id: number; name: string; parent_id: any };

    if (this.data['multiple']) {
      response = this.dataSource.selection.selected;
    } else {
      if (this.dataSource.selection.selected.length) {
        const selectedId = this.dataSource.selection.selected[0];

        response = {
          id: selectedId,
          name: this.selectedAssetType?.name || '',
          parent_id: this.selectedAssetType?.parent_id || null
        }
      } else {
        response = 0;
      }
    }

    this.dialogRef.close(response);
  }

  /**
   * Filters the data source by a specified parent ID and updates the displayed columns.
   *
   * @param {number} parent_id - The ID of the parent to filter by.
   * @return {void}
   */
  onFilterByParent(parent_id: number): void {
    this.dataSource.parent_id = parent_id;
    this.displayedColumns.pop();
    this.dataSource.getData(true);
  }

  /**
   * Reset the list by setting parent_id to null, adding 'actions' column to displayed columns, and fetching data from the data source.
   *
   * @return {void}
   */
  onResetList(): void {
    this.dataSource.parent_id = null;
    this.displayedColumns.push('actions');
    this.dataSource.getData(true);
  }

  /**
   * Closes the dialog.
   *
   * @return {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Create a new asset type.
   *
   * @returns {void}
   */
  onCreate(): void {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create asset types.');
      return;
    }

    this.utils.showComponentDialog(AssetTypesEditComponent,
      {
          asset_type_id: null,
          on_parent_list: !this.dataSource.parent_id,
          parent_id: this.dataSource.parent_id
      }, {
      width: '900px'
    })
      .then((response) => {
        // Check if the response is an asset type with an id
        if ( response && typeof response.id != 'undefined' && response.id ) {
          this.onAssetTypeSelectionChange(true, response);
        }

        // Refresh the asset types selector list.
        this.dataSource.getData();
      });
  }

  /**
   * Open the user's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
      }
    );
  }
}


export class AssetTypesSelectorDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  archived = "false";
  parent_id: number;

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/asset-types', resetOffset, {
      archived: this.archived,
      parent_id: this.parent_id
    });
  }
}
